import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
    viewChild
} from '@angular/core';
import {fromEvent, Observable, Subject} from 'rxjs';
import {debounceTime, filter, takeUntil, tap} from 'rxjs/operators';
import {dependOn} from '../../../../../common/rxjs/depend-on';
import {destroy$} from '../../../../../components/destroyable';
import {DynamicComponentRef} from '../../../../../components/dynamic-component';
import {ElementRulerDirective} from '../../../../../components/element-ruler';
import {Point} from '../../../../../components/layout/abstract-units';
import {Widget} from '../../../../../components/layout/widget/widget';
import {OverlayService} from '../../../../../components/overlay';
import {WindowRefService} from '../../../../../services/window-ref.service';
import {FloatLayoutService} from '../../../float-layout/float-layout.service';
import {LayoutSharedService, LayoutUpdateEvent, LayoutUpdateReason} from '../../layout-shared-service';

interface Rect {
    x: number;
    y: number;
    w: number;
    h: number;
}

type PlacementSide = 'right' | 'left' | 'bottom' | 'top' | 'overlay';

interface Placement {
    side: PlacementSide;
    x: number;
    y: number;
}

// Widget Settings Offset
const WS_OFFSET = {x: 4, y: 4} as const;

@Component({
    selector:    'widget-settings',
    templateUrl: './widget-settings.component.html',
    styleUrls:   [
        './widget-settings.component.scss',
        './widget-settings-common.scss'
    ],
    host:        {
        // .px is a unit suffix; null/undefined in a style binding REMOVES the property,
        // which is a ready-made "not measured yet" state without junk left:0px
        '[style.left.px]':  'placement()?.x ?? null',
        '[style.top.px]':   'placement()?.y ?? null',
        '[attr.data-side]': 'placement()?.side ?? null'
    },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone:  false
})
export class WidgetSettingsComponent implements OnInit, OnDestroy, AfterViewChecked {

    public readonly widget = input.required<Widget>();
    public readonly dynComponentRef = input.required<DynamicComponentRef<WidgetSettingsComponent>>();
    public readonly propagateClose = output<void>();

    public readonly contentBodyRuler = viewChild.required('contentBodyEl', {read: ElementRulerDirective});

    public readonly elementRef = inject(ElementRef); // Public since it's used in the template.

    /** null until the panel is measured (not inserted into DOM / display:none) */
    protected readonly placement = computed<Placement | null>(() => {
        const [bw, bh] = this.boxSize();
        if (bw === 0 || bh === 0) {
            return null;
        }

        const cell = this.widget().cell;
        const [lw, lh] = this.layoutSize();

        return computePlacement(
            cell
                ? {x: cell.display.pos[0], y: cell.display.pos[1], w: cell.display.size[0], h: cell.display.size[1]}
                : {x: 0, y: 0, w: lw, h: lh},
            {x: 0, y: 0, w: bw, h: bh},
            {x: 0, y: 0, w: lw, h: lh},
            WS_OFFSET
        );
    });

    private readonly destroy$ = destroy$();

    private isInitialized = false;

    private readonly delayedUpdate$ = new Subject<void>();

    private readonly clickOutsideService = inject(OverlayService);
    private readonly windowRefService = inject(WindowRefService);
    private readonly floatLayoutService: FloatLayoutService = inject(FloatLayoutService, {optional: true});
    private readonly layoutSharedService = inject(LayoutSharedService, {optional: true});

    private readonly layoutSize = signal<Point>(this.getLayoutSize());
    private readonly boxSize = signal<Point>([0, 0]);

    public ngOnInit(): void {
        // hide all floating widgets
        if (this.floatLayoutService) {
            this.floatLayoutService.hideAllCellComponents();
        }

        this.listenToLayoutUpdates();
        this.listenToUpdateTrigger();
        this.createOverlay();
    }

    public ngAfterViewChecked(): void {
        if (!this.isInitialized) {
            this.isInitialized = true;
            this.delayedUpdate$.next();
        }
    }

    public ngOnDestroy(): void {
        // show all floating widgets
        if (this.floatLayoutService) {
            this.floatLayoutService.showAllCellComponents();
        }
    }

    public close(): void {
        this.propagateClose.emit();
    }

    private listenToLayoutUpdates(): void {
        this.getLayoutResizeStream().pipe(
            takeUntil(this.destroy$),
            tap(() => this.layoutSize.set(this.getLayoutSize()))
        ).subscribe();
    }

    private createOverlay(): void {
        this.clickOutsideService.create({
            target: this.dynComponentRef()
        }).subscribe(() => this.close());
    }

    private getLayoutSize(): Point {
        return this.layoutSharedService
            ? this.layoutSharedService.getLayoutSize()
            : [this.windowRefService.nativeWindow.innerWidth, this.windowRefService.nativeWindow.innerHeight];
    }

    private getLayoutResizeStream(): Observable<any> {
        return this.layoutSharedService
            ? this.layoutSharedService.update$.pipe(
                filter((event: LayoutUpdateEvent) => event.reason === LayoutUpdateReason.RESIZE)
            )
            : fromEvent(this.windowRefService.nativeWindow, 'resize');
    }

    private listenToUpdateTrigger(): void {
        const triggerListening$ = this.delayedUpdate$.pipe(
            debounceTime(0),
            tap(() => {
                const wsElement: HTMLElement = this.elementRef.nativeElement;
                const wsSize: Point = [wsElement.clientWidth, wsElement.clientHeight];
                this.boxSize.set(wsSize);
            })
        );

        const contentResizes$ = this.contentBodyRuler().size$.pipe(
            tap(() => this.delayedUpdate$.next())
        );

        contentResizes$.pipe(
            dependOn(triggerListening$),
            takeUntil(this.destroy$)
        ).subscribe();
    }
}

/**
 * Candidate order is side priority,
 * overlay is an unconditional fallback.
 */
function computePlacement(widget: Rect, box: Rect, layout: Rect, offset: { x: number; y: number }): Placement {
    // Vertical position when placing on the side: align to the top of the widget,
    // but do not go past the bottom edge of layout (the original ternary is equivalent to min),
    // and not above zero (if panel is taller than layout, align to top).
    const sideY = Math.max(Math.min(widget.y + offset.y, layout.h - box.h - offset.y), 0);

    // Horizontal position when placing below/above: the right edge of the panel
    // matches the right edge of the widget minus offset, not less than zero.
    const stackX = Math.max(widget.x + widget.w - offset.x - box.w, 0);

    const candidates: Array<{ fits: boolean; placement: Placement }> = [
        {
            fits:      widget.x + widget.w + offset.x + box.w <= layout.w,
            placement: {side: 'right', x: widget.x + widget.w + offset.x, y: sideY}
        },
        {
            fits:      widget.x - box.w - offset.x >= 0,
            placement: {side: 'left', x: widget.x - box.w - offset.x, y: sideY}
        },
        {
            fits:      widget.y + widget.h + offset.y + box.h <= layout.h,
            placement: {side: 'bottom', x: stackX, y: widget.y + widget.h + offset.y}
        },
        {
            fits:      widget.y - box.h - offset.y >= 0,
            placement: {side: 'top', x: stackX, y: widget.y - box.h - offset.y}
        },
        {
            fits:      true, // overlay is a fallback
            placement: {side: 'overlay', x: Math.max(Math.round(layout.w - box.w), 0), y: sideY}
        }
    ];

    return (
        candidates.find(c => c.fits)
        ?? candidates[candidates.length - 1]
    ).placement;
}
